import * as selecaoInscricao from './selecaoInscricao';
import * as comissoes from './comissoes';
import * as atualizacaoCadastral from './atualizacaoCadastral';
import * as certidoes from './certidoes';
import { getStartingPaymentInfo } from '../core/services/pagamentoService';

// Registro de todos os departamentos
const DEPARTAMENTOS = {
  SELECAO_INSCRICAO: {
    id: 'selecao-inscricao',
    nome: 'Seleção e Inscrição',
    descricao: 'Requerimentos relacionados à inscrição na OAB/MS',
    rota: '/requerimentos/selecao-inscricao',
    configs: selecaoInscricao,
  },
  COMISSOES: {
    id: 'comissoes',
    nome: 'Comissões',
    descricao: 'Inscrição e participação nas Comissões da OAB/MS',
    rota: '/requerimentos/comissoes',
    configs: comissoes,
  },
  ATUALIZACAO_CADASTRAL: {
    id: 'atualizacao-cadastral',
    nome: 'Atualização Cadastral',
    descricao: 'Atualização de dados cadastrais e de subseção junto à OAB/MS',
    rota: '/requerimentos/atualizacao-cadastral',
    configs: atualizacaoCadastral,
  },
  CERTIDOES: {
    id: 'certidoes',
    nome: 'Certidões',
    descricao: 'Solicite e acompanhe emissão de certidões junto à OAB/MS',
    rota: '/requerimentos/certidoes',
    configs: certidoes,
  },
};

// Dados de um departamento (nome, rota...) a partir do id usado na URL
export const getDepartamento = (departamentoId) =>
  Object.values(DEPARTAMENTOS).find(d => d.id === departamentoId) || null;

// Função para obter configuração de qualquer pedido
export const getPedidoConfig = (departamentoId, pedidoId) => {
  const dept = Object.values(DEPARTAMENTOS).find(d => d.id === departamentoId);
  
  if (!dept) {
    return null;
  }
  
  const config = dept.configs[pedidoId];
  return config || null;
};

// Função para listar pedidos de um departamento
export const getPedidosByDepartamento = (departamentoId) => {
  const dept = Object.values(DEPARTAMENTOS).find(d => d.id === departamentoId);
  if (!dept) return [];

  return Object.entries(dept.configs)
    .map(([id, config]) => {
      const { amount, isVariable } = getStartingPaymentInfo(config.paymentConfig);

      return {
        id,
        nome: config.nome,
        descricao: config.descricao,
        // Descrição alternativa pra quem está logado como PJ (ver adaptarConfigAoPerfil)
        descricaoPJ: config.descricaoPJ || null,
        icon: config.icon || 'FaFileAlt',
        // Campo opcional: quando presente, define explicitamente em qual categoria
        // da tela de seleção o pedido aparece (ver SelecaoPedidoPage). Pedidos sem
        // "categoria" continuam sendo classificados pelo nome, como antes.
        categoria: config.categoria || null,
        precoBase: amount,
        precoVariavel: isVariable,
        totalDocumentos: config.documentConfig?.required?.length || 0
      };
    });
};

// Índice reverso modeloRequerimento -> pedido, construído uma vez a partir do
// protocoloConfig de cada pedido (ver protocoloResolverService.js para os 2
// formatos possíveis). Usado por quem só tem o modeloRequerimento (a API de
// Pendências, que só enxerga a tabela Requerimento do legado) e precisa
// descobrir de qual pedido do portal ele veio. tipoPedido fica de fora porque
// pode depender da categoria OAB da pessoa (oabData), que não temos aqui — só
// tiposAssunto importa pra quem consome este índice (reenvio de documento) e
// esse campo nunca é condicional a nível de pedido inteiro.
const construirIndiceModeloRequerimento = () => {
  const indice = new Map();

  const registrar = (modeloRequerimento, entrada) => {
    if (modeloRequerimento === undefined || modeloRequerimento === null) return;
    indice.set(String(modeloRequerimento), entrada);
  };

  Object.values(DEPARTAMENTOS).forEach((dept) => {
    Object.entries(dept.configs).forEach(([pedidoId, config]) => {
      const { protocoloConfig } = config;
      if (!protocoloConfig) return;

      const base = { departamentoId: dept.id, pedidoId, nome: config.nome, config };

      // Formato 2: modeloRequerimento/tiposAssunto/tipoPedido variam juntos pela mesma opção
      if (protocoloConfig.tiposAssunto === undefined) {
        Object.values(protocoloConfig.map || {}).forEach((opcao) => {
          registrar(opcao.modeloRequerimento, { ...base, tiposAssunto: opcao.tiposAssunto });
        });
        return;
      }

      // Formato 1: tiposAssunto fixo; modeloRequerimento pode ser fixo ou condicional
      const { modeloRequerimento, tiposAssunto } = protocoloConfig;

      if (typeof modeloRequerimento === 'string') {
        registrar(modeloRequerimento, { ...base, tiposAssunto });
      } else if (modeloRequerimento?.map) {
        Object.values(modeloRequerimento.map).forEach((valor) => {
          registrar(valor, { ...base, tiposAssunto });
        });
      }
    });
  });

  return indice;
};

const INDICE_MODELO_REQUERIMENTO = construirIndiceModeloRequerimento();

// Resolve departamento/pedido/tiposAssunto a partir do modeloRequerimentoId
// devolvido por GET /pendencias/:pessoaId. Retorna null se não achar (modelo
// legado sem pedido correspondente no portal, ou catálogo desatualizado).
export const getPedidoPorModeloRequerimento = (modeloRequerimentoId) => {
  if (modeloRequerimentoId === undefined || modeloRequerimentoId === null) return null;
  return INDICE_MODELO_REQUERIMENTO.get(String(modeloRequerimentoId)) || null;
};
